'use client'

import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import { Download, X, Share, Plus } from 'lucide-react'

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const DISMISS_KEY = 'rezaro:pwa-install-dismissed-at'
const DISMISS_TTL_MS = 14 * 24 * 60 * 60 * 1000 // 14 days

function recentlyDismissed(): boolean {
  try {
    const v = localStorage.getItem(DISMISS_KEY)
    if (!v) return false
    return Date.now() - parseInt(v, 10) < DISMISS_TTL_MS
  } catch {
    return false
  }
}

/** iOS Safari only — no `beforeinstallprompt`, no PWA already installed. */
function isIosSafariNeedingPrompt(): boolean {
  if (typeof window === 'undefined') return false
  const ua = navigator.userAgent
  const isIos = /iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua)
  if (!isIos) return false
  // navigator.standalone is true only when launched from the home-screen.
  const standalone = (navigator as Navigator & { standalone?: boolean }).standalone === true
    || window.matchMedia('(display-mode: standalone)').matches
  return !standalone
}

export function PwaInstallPrompt() {
  const pathname = usePathname() ?? ''
  const [event, setEvent] = useState<InstallPromptEvent | null>(null)
  const [shown, setShown] = useState(false)
  const [iosPrompt, setIosPrompt] = useState(false)

  // Suppress on:
  //  • /backstage — internal ops console, overlapped the auth form
  //  • conversion-critical public pages with a primary bottom CTA
  //    (booking widget, gift-voucher purchase, review flow) where a
  //    centered bottom banner literally covers the "Jetzt buchen /
  //    kaufen" button on mobile and tanks conversion.
  const CONVERSION_SUBROUTE = /^\/[^/]+\/(widget|gutschein|bewerten)(\/|$)/
  // Auth pages: the centered bottom banner sat exactly on top of the
  // "Anmelden" submit button on laptop viewports — sign-in was unclickable
  // until the banner was dismissed.
  const AUTH_ROUTE = /^(\/(en|tr))?\/(signin|signup)(\/|$)/
  const suppressed = pathname.startsWith('/backstage')
    // Guest-facing QR menu: diners scanning a table QR must never be asked
    // to install the RESTAURANT-OWNER app (it also covered the cart button).
    || pathname.startsWith('/menu/')
    || CONVERSION_SUBROUTE.test(pathname)
    || AUTH_ROUTE.test(pathname)

  useEffect(() => {
    // Register the service worker once we're hydrated.
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/sw.js', { scope: '/' })
        .catch(() => { /* sw is best-effort */ })
    }

    if (suppressed) return
    if (recentlyDismissed()) return

    function onBeforeInstall(e: Event) {
      e.preventDefault()
      setEvent(e as InstallPromptEvent)
      setShown(true)
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstall)

    // iOS Safari: there's no `beforeinstallprompt`. If we're on a real
    // iPhone Safari (not already standalone, not Chrome/Firefox iOS),
    // schedule an instructional banner once the user has spent some
    // time on the page so we don't shove it in their face on first paint.
    let iosTimer: ReturnType<typeof setTimeout> | undefined
    if (isIosSafariNeedingPrompt()) {
      iosTimer = setTimeout(() => setIosPrompt(true), 8_000)
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall)
      if (iosTimer) clearTimeout(iosTimer)
    }
  }, [suppressed])

  function dismiss() {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())) } catch { /* ignore */ }
    setShown(false)
    setIosPrompt(false)
  }

  async function install() {
    if (!event) return
    await event.prompt()
    await event.userChoice
    setEvent(null)
    setShown(false)
  }

  if (suppressed) return null

  // Chrome / Android — native install prompt path
  if (shown && event) {
    return (
      <div className="fixed bottom-20 md:bottom-4 left-1/2 -translate-x-1/2 z-30 w-[min(96vw,420px)] rounded-2xl border border-neutral-200 bg-white shadow-2xl p-4 flex items-center gap-3">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-brand-100 text-brand-600">
          <Download className="h-5 w-5" strokeWidth={2.2} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-neutral-900">Rezaro auf dem Startbildschirm</p>
          <p className="text-[12px] text-neutral-500 mt-0.5">
            Schneller Zugriff, Offline-Modus und Benachrichtigungen — direkt vom Home-Screen.
          </p>
        </div>
        <button
          type="button"
          onClick={install}
          className="rounded-lg bg-brand-600 px-3 py-2 text-xs font-semibold text-white hover:bg-brand-700 transition-colors flex-shrink-0">
          Hinzufügen
        </button>
        <button
          type="button"
          onClick={dismiss}
          title="Schließen"
          className="rounded-lg p-1.5 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors flex-shrink-0">
          <X className="h-4 w-4" />
        </button>
      </div>
    )
  }

  // iOS Safari — show a visual instruction banner since there's no prompt API
  if (iosPrompt) {
    return (
      <div className="fixed bottom-20 md:bottom-4 left-1/2 -translate-x-1/2 z-30 w-[min(96vw,440px)] rounded-2xl border border-neutral-200 bg-white shadow-2xl overflow-hidden">
        <div className="flex items-start gap-3 p-4">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-brand-100 text-brand-600">
            <Download className="h-5 w-5" strokeWidth={2.2} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-neutral-900">Rezaro als App auf dem iPhone</p>
            <p className="text-[12px] text-neutral-500 mt-0.5">
              In Safari unten auf <Share className="inline h-3.5 w-3.5 mx-0.5 align-text-bottom text-brand-600" />
              tippen → „Zum Home-Bildschirm" <Plus className="inline h-3.5 w-3.5 mx-0.5 align-text-bottom text-neutral-600" />.
            </p>
          </div>
          <button
            type="button"
            onClick={dismiss}
            title="Schließen"
            className="rounded-lg p-1.5 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors flex-shrink-0">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    )
  }

  return null
}
